"use client"
import { useState, useEffect, useRef, useCallback } from "react"
import BotMessage from "./BotMessage"
import UserMessage from "./UserMessage"
import OtherQueryInput from "./OtherQueryInput"
import "./ChatContainer.css"
import {
  getClientDetails,
  verifyMobileNumber,
  getUserDevices,
  getComplaintsByProductId,
  createVisitorTicket,
  createAmcExpiredTicket,
  createAmcCallServiceTicket,
  getRMSAreasAndDevices,
  scheduleRMSTicket,
  fetchQuestions,
  submitInquiry,
  getClientContactNumber,
} from "../services/api"

const ChatContainer = ({ config, clientId: propsClientId }) => {
  const urlClientId = typeof window !== "undefined" ? window.location.pathname.split("/chatbot/")[1] : null
  const clientId = propsClientId || urlClientId
  // ============================================
  // CORE CHAT STATES
  // ============================================
  const [messages, setMessages] = useState([])
  const [showOptions, setShowOptions] = useState(false)
  const [showContactInput, setShowContactInput] = useState(false)
  const [contact, setContact] = useState("")
  const [verifiedContact, setVerifiedContact] = useState("")
  const [isVerifyingContact, setIsVerifyingContact] = useState(false)
  const [showTypingEffect, setShowTypingEffect] = useState(false)
  // ============================================
  // USER & CLIENT STATES
  // ============================================
  const [userId, setUserId] = useState(null)
  const [clientName, setClientName] = useState("")
  const [isClientLoaded, setIsClientLoaded] = useState(false)
  const [initialMessagesSet, setInitialMessagesSet] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  // ============================================
  // FLOW SELECTION & SERVICE STATES
  // ============================================
  const [isExistingClient, setIsExistingClient] = useState(null)
  const [flowSelection, setFlowSelection] = useState(null)
  const [selectedDevice, setSelectedDevice] = useState(null)
  const [selectedComplaintDetails, setSelectedComplaintDetails] = useState([])
  const [selectedComplaintDetail, setSelectedComplaintDetail] = useState("")
  const [storedComplaintIDs, setStoredComplaintIDs] = useState([])
  const [amcStep, setAmcStep] = useState("")
  // ============================================
  // RMS STATES
  // ============================================
  const [rmsAreas, setRmsAreas] = useState([])
  const [selectedArea, setSelectedArea] = useState(null)
  const [rmsDevicesInArea, setRmsDevicesInArea] = useState([])
  const [isSchedulingRMS, setIsSchedulingRMS] = useState(false)
  // ============================================
  // LEADBOT/SALES DYNAMIC FLOW STATES
  // ============================================
  const [salesFlowActive, setSalesFlowActive] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [questionHistory, setQuestionHistory] = useState([])
  const [isOtherSelected, setIsOtherSelected] = useState(false)
  const [userQuery, setUserQuery] = useState("")
  const [pendingQuery, setPendingQuery] = useState("")
  const [isQueryInputDisabled, setQueryInputDisabled] = useState(false)
  const [isQuerySubmitted, setIsQuerySubmitted] = useState(false)
  const [isDetailsFormVisible, setIsDetailsFormVisible] = useState(false)
  const [isDetailsRequested, setIsDetailsRequested] = useState(false)
  const [isDetailsSubmitted, setIsDetailsSubmitted] = useState(false)
  const [userInfo, setUserInfo] = useState({ name: "", mobile: "", email: "" })
  const [validationErrors, setValidationErrors] = useState({})
  // ============================================
  // LEADBOTCALLBACK & RATING STATES
  // ============================================
  const [showCallbackMessage, setShowCallbackMessage] = useState(false)
  const [callbackPreference, setCallbackPreference] = useState(null)
  const [callbackRequested, setCallbackRequested] = useState(false)
  const [showRestartPrompt, setShowRestartPrompt] = useState(false)
  const [showPhoneCallAfterNoCallback, setShowPhoneCallAfterNoCallback] = useState(false)
  const [supportPhoneNumber, setSupportPhoneNumber] = useState("")
  // ============================================
  // UI STATES
  // ============================================
  const [showEndMenuOptions, setShowEndMenuOptions] = useState(false)
  const [hasShownMainMenu, setHasShownMainMenu] = useState(false)
  const [contactAttempts, setContactAttempts] = useState(0)
  const [conversationCompleted, setConversationCompleted] = useState(false)
  const [lastBotMessage, setLastBotMessage] = useState("")
  const [showReminder, setShowReminder] = useState(false)
  const inactivityTimerRef = useRef(null)
  const messagesEndRef = useRef()
  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.value = 800
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.5)
    } catch (error) {
      console.error("Error playing notification sound:", error)
    }
  }, [])

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const validateName = (name) => {
    if (!name || !String(name).trim()) return "Name is required"

    const clean = String(name).trim().normalize("NFC")

    if (clean.length < 2) return "Name must be at least 2 characters"

    try {
      const unicodeRegex = /^\p{L}[\p{L}\s.'-]*$/u
      if (unicodeRegex.test(clean)) return ""
    } catch (e) { }

    const fallbackRegex = /^[A-Za-z\u0900-\u097F\s.'-]+$/
    if (!fallbackRegex.test(clean)) return "Name can only contain letters and spaces"

    return ""
  }

  const validateMobile = (mobile) => {
    if (!mobile.trim()) return "Mobile number is required"
    if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      return "Mobile number must start with 6-9 and be exactly 10 digits"
    }
    return ""
  }

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required"
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) return "Please enter a valid email address"
    return ""
  }

  const safeSetMessage = (newMessage) => {
    const messageWithTimestamp = { ...newMessage, timestamp: Date.now() }
    if (newMessage.type === "bot" || newMessage.type === "working-hours") {
      const isDuplicate = messages.some((msg) => {
        return (
          (msg.type === "bot" || msg.type === "working-hours") &&
          msg.text === newMessage.text &&
          (!msg.options || JSON.stringify(msg.options) === JSON.stringify(newMessage.options))
        )
      })
      if (isDuplicate) return
      setLastBotMessage(newMessage.text)
    }
    setMessages((prev) => [...prev, messageWithTimestamp])
  }

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    if (!clientId) return

    const loadClient = async () => {
      try {
        const res = await getClientDetails(clientId)
        const name = res?.data?.ClientName || res?.data?.Name || ""

        if (name) {
          setClientName(name)
        }
      } catch (err) {
        console.error("Error loading client details in ChatContainer:", err)
      } finally {
        setIsClientLoaded(true)
      }
    }
    loadClient()
  }, [clientId])

  const showInitialMenu = () => {
    safeSetMessage({
      type: "bot",
      text: "Please choose how we can assist you:",
      options: ["I am an existing AMC / warranty customer", "I am looking for a new Sales or Service"],
      timestamp: Date.now(),
    })
    setShowOptions(true)
    setSalesFlowActive(false)
  }
  useEffect(() => {
    if (!isClientLoaded || initialMessagesSet) return

    showInitialMenu()
    setInitialMessagesSet(true)
  }, [isClientLoaded, initialMessagesSet])

  useEffect(() => {
    scrollToBottom()
  }, [messages, showOptions, showContactInput, showEndMenuOptions, isDetailsFormVisible])

  useEffect(() => {
    const loadContact = async () => {
      if (clientId) {
        const number = await getClientContactNumber(clientId)
        if (number) setSupportPhoneNumber(number)
      }
    }
    loadContact()
  }, [clientId])

  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
    inactivityTimerRef.current = setTimeout(
      () => {
        setShowReminder(true)
      },
      5 * 60 * 1000,
    )
  }, [])

  const stopInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
  }, [])

  useEffect(() => {
    if (isOpen) startInactivityTimer()
    else stopInactivityTimer()
  }, [isOpen, startInactivityTimer, stopInactivityTimer])

  // ============================================
  // SERVICE FLOW - TICKET GENERATION
  // ============================================
  const submitVisitorTicket = async ({ deviceID, complaintIDs, userId }) => {
    try {
      const payload = { deviceID: String(deviceID), complaintIDs: complaintIDs.map(String), createdBy: userId || 0 }
      const result = await createVisitorTicket(payload)

      if (!result?.status && result?.message?.includes("AMC")) {
        return { status: false, message: result.message, isAMCExpired: true }
      }
      if (!result?.status) {
        return { status: false, message: result.message || "Ticket creation failed." }
      }

      safeSetMessage({
        type: "bot",
        text: `Thank you for contacting ${clientName || "us"}. Your service request has been generated. Our engineer will connect with you shortly!`,
      })
      return { status: true, message: "Ticket created successfully", data: result }
    } catch (error) {
      console.error("Ticket submission failed:", error)
      const errMsg =
        error?.response?.data?.message || error?.message || "Ticket creation failed. Please try again later."
      safeSetMessage({ type: "bot", text: errMsg })
      return { status: false, message: errMsg }
    }
  }

  const handleDeviceSelect = async (deviceID, productId) => {
    setSelectedDevice({ deviceID, productId })
    setSelectedComplaintDetail("")
    setSelectedComplaintDetails([])
    safeSetMessage({ type: "user", text: `Device ID: ${deviceID}` })

    try {
      const res = await getComplaintsByProductId(productId)
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        setMessages((prev) => [...prev, { type: "complaint-options", complaints: res.data, timestamp: Date.now() }])
      } else {
        safeSetMessage({ type: "bot", text: "No complaint categories found for this product." })
        setTimeout(() => showEndMenu(), 1000)
      }
    } catch (err) {
      console.error("Error loading complaints:", err)
      safeSetMessage({ type: "bot", text: "Failed to load complaint categories." })
      setTimeout(() => showEndMenu(), 1000)
    }
  }

  const handleComplaintSelect = async (complaintTexts) => {
    const complaintsText = Array.isArray(complaintTexts) ? complaintTexts.join(", ") : complaintTexts
    const { deviceID } = selectedDevice

    safeSetMessage({ type: "user", text: complaintsText })

    const complaintOptionsMsgObj = messages.filter((msg) => msg.type === "complaint-options").pop()
    if (!complaintOptionsMsgObj) return

    const complaintIDs = complaintTexts
      .map((complaintText) => {
        const selectedComplaint = complaintOptionsMsgObj.complaints.find((c) => c.ComplaintDetail === complaintText)
        return selectedComplaint ? selectedComplaint.id : null
      })
      .filter((id) => id !== null)

    if (!complaintIDs.length) return
    setStoredComplaintIDs(complaintIDs)

    try {
      const response = await submitVisitorTicket({ deviceID, complaintIDs, userId: userId || 0 })

      if (response?.status === false && response?.message?.includes("AMC")) {
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: response.message, timestamp: Date.now() },
          {
            type: "bot",
            text: `Your AMC for this device has expired with ${clientName || "us"}. Please select your preference:`,
            options: ["Renew AMC", "Go with Call Basis Services"],
            timestamp: Date.now(),
          },
        ])
        setAmcStep("amc-expired")
        setShowOptions(true)
        return
      }

      if (response?.status) {
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            text: "Do you have any issue regarding same device?",
            options: ["Re-initiate Conversation", "End Session"],
            timestamp: Date.now(),
          },
        ])
        setShowOptions(true)
      } else {
        safeSetMessage({ type: "bot", text: response?.message || "Ticket creation failed." })
        setTimeout(() => showEndMenu(), 1000)
      }
    } catch (error) {
      console.error("Create ticket failed:", error)
      const errMsg = error.response?.data?.message || "Network error while creating ticket."
      safeSetMessage({ type: "bot", text: errMsg })
      setTimeout(() => showEndMenu(), 1000)
    }
  }

  // ============================================
  // RMS FLOW - SCHEDULE MAINTENANCE
  // ============================================
  const handleRMSAreaSelect = (areaCode) => {
    setSelectedArea(areaCode)
    const selectedAreaDetails = rmsAreas.find((area) => area.area_code === areaCode)
    if (selectedAreaDetails) {
      setRmsDevicesInArea(selectedAreaDetails.devices)
      safeSetMessage({ type: "user", text: `Selected Area: ${areaCode}` })
      const deviceIds = selectedAreaDetails.devices.map((device) => device.deviceID).join(", ")
      safeSetMessage({ type: "bot", text: `Device IDs in area ${areaCode}: ${deviceIds}` })
      safeSetMessage({
        type: "bot",
        text: `Would you like to schedule RMS for ${areaCode}?`,
        options: ["Schedule RMS", "Restart Conversation"],
        timestamp: Date.now(),
      })
      setShowOptions(true)
    } else {
      safeSetMessage({ type: "bot", text: "Selected area details not found. Please try again." })
      setTimeout(() => showEndMenu(), 1000)
    }
  }

  const handleScheduleRMS = async () => {
    if (!selectedArea || !userId || isSchedulingRMS) return
    safeSetMessage({ type: "user", text: "Schedule RMS" })
    setIsSchedulingRMS(true)
    setShowOptions(false)
    try {
      const response = await scheduleRMSTicket({
        userId,
        areaCode: selectedArea,
        ClientId: clientId,
        productID: 33,
        complaintID: 106,
      })
      if (response.status) {
        safeSetMessage({
          type: "bot",
          text: `Your RMS for area ${selectedArea} has been scheduled successfully! Our team will contact you shortly.`,
        })
        setConversationCompleted(true)
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text: "What would you like to do next?",
              options: ["Restart Conversation", "End Session"],
              timestamp: Date.now(),
            },
          ])
          setShowOptions(true)
        }, 1500)
      } else {
        safeSetMessage({
          type: "bot",
          text: `Failed to schedule RMS: ${response.message || "Unknown error."}`,
        })
        setTimeout(() => showEndMenu(), 1000)
      }
    } catch (error) {
      console.error("Error scheduling RMS:", error)
      safeSetMessage({
        type: "bot",
        text: "There was an error scheduling your RMS. Please try again later.",
      })
      setTimeout(() => showEndMenu(), 1000)
    } finally {
      setIsSchedulingRMS(false)
    }
  }

  // ============================================
  // SALES/LEADBOT FLOW - DYNAMIC QUESTIONS
  // ============================================
  const initializeSalesFlow = async () => {
    setShowTypingEffect(true)

    try {
      if (!clientId) throw new Error("Client ID is missing")

      const data = await fetchQuestions(clientId)

      if (!data || !data.questions || data.questions.length === 0) {
        throw new Error("No questions found for this client")
      }

      // Pick the first-level question (root)
      let firstQuestion = data.questions.find((q) => q.question_level === 1 && q.question_parent_level === 0)
      if (!firstQuestion && data.questions.length > 0) {
        firstQuestion = data.questions.reduce((prev, curr) => (prev.question_level < curr.question_level ? prev : curr))
      }

      if (!firstQuestion) throw new Error("No valid first question found")

      // If firstQuestion is a leaf (has no children) -> directly show textarea
      const firstHasChildren = Array.isArray(firstQuestion.children) && firstQuestion.children.length > 0

      if (!firstHasChildren) {
        // There is just a single parent question with no children -> treat as leaf and open textarea
        setCurrentQuestion(firstQuestion)
        // Save context
        setPendingQuery(firstQuestion.question_text || "")
        // push a bot message prompting query and open textarea
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            text: firstQuestion.question_text || "Please describe your query:",
            timestamp: Date.now(),
          },

        ])
        setIsOtherSelected(true)
        setShowOptions(false)
        setSalesFlowActive(true)
        setShowTypingEffect(false)
        return
      }

      // If firstQuestion has exactly one child and that child is a leaf -> auto open textarea for that child
      if (firstQuestion.children.length === 1) {
        const onlyChild = firstQuestion.children[0]
        const childHasChildren = Array.isArray(onlyChild.children) && onlyChild.children.length > 0
        if (!childHasChildren) {
          // Set current to only child and open textarea directly
          setCurrentQuestion(onlyChild)
          setPendingQuery(onlyChild.question_text || "")
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text: onlyChild.question_text || "Please describe your requirement:",
              timestamp: Date.now(),
            },
            {
              type: "bot",
              text: "To better assist you, please describe your requirement:",
              timestamp: Date.now(),
            },
          ])
          setIsOtherSelected(true)
          setShowOptions(false)
          setSalesFlowActive(true)
          setShowTypingEffect(false)
          return
        }
      }

      // Otherwise show the root question with its children as options (plus Other)
      setCurrentQuestion(firstQuestion)
      setSalesFlowActive(true)
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: firstQuestion.question_text,
          options: [...(firstQuestion.children || []), { id: "other", question_text: "Other" }],
          timestamp: Date.now(),
        },
      ])
      setShowOptions(true)
    } catch (error) {
      console.error("Error initializing Sales flow:", error)
      safeSetMessage({ type: "bot", text: "Unable to load sales options. Please try again later." })
      setTimeout(() => showEndMenu(), 1000)
    } finally {
      setShowTypingEffect(false)
    }
  }

  const handleSalesOptionClick = (selectedOptionLabel, questionIndex) => {
    playNotificationSound()

    if (selectedOptionLabel === "Other") {
      safeSetMessage({ type: "user", text: "Other" })
      safeSetMessage({
        type: "bot",
        text: "To better assist you, please describe your requirement:",
      })
      setIsOtherSelected(true)
      setShowOptions(false)
      return
    }

    const currentOptions = messages[questionIndex]?.options || []
    const selectedOption = currentOptions.find((opt) => opt.question_text === selectedOptionLabel)

    if (selectedOption) {
      handleSalesQuestionSelection(selectedOption, questionIndex)
    }
  }

  // ===========================
  // handleSalesQuestionSelection (handles leaf & single-option auto-open cases)
  // ===========================
  const handleSalesQuestionSelection = (selectedOption, questionIndex) => {
    setShowTypingEffect(true)

    setTimeout(() => {
      const currentMsg = messages[questionIndex]
      if (currentMsg) {
        setQuestionHistory((prev) => [...prev, currentMsg])
      }

      const nextQuestion = currentMsg?.options?.find((q) => q.id === selectedOption.id)

      // Add user's selection as a message
      setMessages((prev) => {
        const newMessages = [...prev]
        if (!newMessages.some((msg) => msg.text === selectedOption.question_text && msg.type === "user")) {
          newMessages.push({ type: "user", text: selectedOption.question_text, timestamp: Date.now() })
        }

        // Only push a bot next question if it's a non-leaf
        if (nextQuestion && nextQuestion.children && nextQuestion.children.length > 0) {
          newMessages.push({
            type: "bot",
            text: nextQuestion.question_text,
            options: [...nextQuestion.children, { id: "other", question_text: "Other" }],
            timestamp: Date.now(),
          })
        }
        return newMessages
      })

      // Detect leaf (no children) -> open textarea immediately
      const isLeaf = !nextQuestion || !nextQuestion.children || nextQuestion.children.length === 0

      if (isLeaf) {
        setShowTypingEffect(false)
        // store context
        setCurrentQuestion(selectedOption)
        setPendingQuery(selectedOption.question_text || "")
        setIsOtherSelected(true)
        setShowOptions(false)
        return
      }

      // Otherwise, continue flow
      setCurrentQuestion(nextQuestion)
      setShowTypingEffect(false)
    }, 900)
  }

  const handleCallbackSelection = async (preference) => {
    setShowTypingEffect(true)
    playNotificationSound()

    setTimeout(() => {
      setCallbackPreference(preference)
      setShowCallbackMessage(false)
      safeSetMessage({ type: "user", text: preference })

      const botMessage =
        preference === "Yes"
          ? "Our representatives will reach out to you shortly."
          : "Alright! Let us know if you need any help later."

      safeSetMessage({ type: "bot", text: botMessage })

      if (preference === "Yes") {
        setTimeout(() => {
          setIsDetailsFormVisible(true)
        }, 1000)
      } else {
        setShowPhoneCallAfterNoCallback(true)
        setShowRestartPrompt(true)
      }
      setShowTypingEffect(false)
    }, 1000)
  }

  const handleUserDetailsSubmit = async (details) => {
    setShowTypingEffect(true)

    if (!details || !details.name || !details.mobile || !details.email) {
      console.error("Invalid user details:", details)
      setShowTypingEffect(false)
      return
    }

    const nameError = validateName(details.name)
    const mobileError = validateMobile(details.mobile)
    const emailError = validateEmail(details.email)

    if (nameError || mobileError || emailError) {
      setValidationErrors({ name: nameError, mobile: mobileError, email: emailError })
      setShowTypingEffect(false)
      return
    }

    try {
      const inquiryData = {
        Client_name: details.name,
        contact: details.mobile,
        email: details.email,
        last_question: currentQuestion?.id || 0,
        agent_remarks: "",
        Next_followup: "",
        Client_id: clientId,
        other_query: userQuery || pendingQuery,
      }

      const inquiryResponse = await submitInquiry(inquiryData)

      const newMessages = [
        { type: "user", text: `Name: ${details.name}`, timestamp: Date.now() },
        { type: "user", text: `Mobile: ${details.mobile}`, timestamp: Date.now() },
        { type: "user", text: `Email: ${details.email}`, timestamp: Date.now() },
        {
          type: "bot",
          text: "Thank you for sharing your details! Our representatives will get in touch with you shortly.",
          timestamp: Date.now(),
        },
      ]

      setMessages((prev) => [...prev, ...newMessages])
      setIsDetailsSubmitted(true)
      setIsDetailsFormVisible(false)
      setConversationCompleted(true)

      setTimeout(() => {
        safeSetMessage({
          type: "bot",
          text: `Please check your registered email for details. Thank you for visiting ${clientName || "us"}. Our working hours are: Monday to Friday, 10:00 AM to 5:00 PM. Our Admin will contact you.`,
        })
        setShowRestartPrompt(true)
      }, 1000)
    } catch (error) {
      console.error("Failed to submit inquiry:", error)
      safeSetMessage({ type: "bot", text: "Failed to submit your inquiry. Please try again later." })
      setTimeout(() => {
        safeSetMessage({
          type: "bot",
          text: "Would you like to try again?",
          options: ["Yes", "No"],
          timestamp: Date.now(),
        })
        setShowOptions(true)
      }, 1000)
    }
    setShowTypingEffect(false)
  }

  const handleUserQuerySubmit = async (query) => {
    setShowTypingEffect(true)
    playNotificationSound()

    if (!query.trim()) {
      setShowTypingEffect(false)
      return
    }

    setTimeout(() => {
      setUserQuery(query)
      safeSetMessage({ type: "user", text: query })
      safeSetMessage({
        type: "bot",
        text: "Thank you! Now, please provide your contact details so we can reach you.",
      })
      setIsDetailsFormVisible(true)
      setShowTypingEffect(false)
    }, 500)
  }

  // ============================================
  // MISC HANDLERS
  // ============================================
  const showEndMenu = (additionalMessage = "") => {
    const workingHoursMessage = `Please check your registered email for Ticket Details.\n\nThank you for visiting ${clientName || "us"}.\n\nOur working hours are:\nMonday to Friday, 10:00 AM to 5:00 PM\n\nOur Admin will contact you.`

    if (!additionalMessage) {
      return
    }

    const messageExists = messages.some((msg) => msg.text === workingHoursMessage)
    if (!messageExists && workingHoursMessage !== lastBotMessage) {
      safeSetMessage({ type: "working-hours", text: workingHoursMessage })
    }

    if (additionalMessage && additionalMessage !== workingHoursMessage) {
      setTimeout(() => {
        const additionalExists = messages.some((msg) => msg.text === additionalMessage)
        if (!additionalExists && additionalMessage !== lastBotMessage) {
          safeSetMessage({ type: "bot", text: additionalMessage })
        }
      }, 1000)
    }

    setTimeout(
      () => {
        setShowEndMenuOptions(true)
        setShowOptions(false)
        setShowContactInput(false)
        setHasShownMainMenu(true)
      },
      additionalMessage ? 2000 : 1000,
    )
  }

  // ============================================
  // SERVICE FLOW - CONTACT VERIFICATION
  // ============================================
  const handleContactSubmit = async () => {
    if (!contact.trim()) return

    const mobileError = validateMobile(contact)
    if (mobileError) {
      setValidationErrors({ contact: mobileError })
      return
    }

    if (contactAttempts >= 5) {
      safeSetMessage({
        type: "bot",
        text: "You have exceeded the maximum number of verification attempts (5). Please contact our support team for assistance.",
      })
      setShowContactInput(false)
      setShowOptions(false)
      setTimeout(() => {
        setShowRestartPrompt(true)
      }, 1000)
      return
    }

    setValidationErrors({})
    safeSetMessage({ type: "user", text: contact })
    setShowContactInput(false)
    setIsVerifyingContact(true)

    try {
      const verifyRes = await verifyMobileNumber(contact, clientId)
      const userIdFromAPI = verifyRes?.user_id

      if (verifyRes.success && userIdFromAPI) {
        setUserId(userIdFromAPI)
        setVerifiedContact(contact)
        setContactAttempts(0)
        setContact("")

        setTimeout(() => {
          safeSetMessage({
            type: "bot",
            text: "What service are you looking for?",
            options: ["Break Down", "RMS (Routine Maintenance Service)"],
            timestamp: Date.now(),
          })
          setShowOptions(true)
        }, 800)
      } else {
        const newAttempts = contactAttempts + 1
        setContactAttempts(newAttempts)

        safeSetMessage({
          type: "bot",
          text: "Verification failed. Please try again.",
        })

        setTimeout(() => {
          if (newAttempts >= 5) {
            safeSetMessage({
              type: "bot",
              text: "You have used all 5 verification attempts. Please contact our support team for assistance.",
            })
            setShowOptions(false)
            setShowContactInput(false)
            setTimeout(() => {
              setShowRestartPrompt(true)
            }, 800)
          } else {
            safeSetMessage({
              type: "bot",
              text: `Would you like to try again? (Attempt ${newAttempts}/5)`,
              options: ["Yes", "No"],
              timestamp: Date.now(),
            })
            setShowOptions(true)
          }
        }, 800)
      }
    } catch (err) {
      console.error("Verification error:", err)
      const newAttempts = contactAttempts + 1
      setContactAttempts(newAttempts)

      safeSetMessage({
        type: "bot",
        text: "Verification failed. Please try again.",
      })

      setTimeout(() => {
        if (newAttempts >= 5) {
          safeSetMessage({
            type: "bot",
            text: "You have used all 5 verification attempts. Please contact our support team for assistance.",
          })
          setShowOptions(false)
          setShowContactInput(false)
          setTimeout(() => {
            setShowRestartPrompt(true)
          }, 800)
        } else {
          safeSetMessage({
            type: "bot",
            text: `Would you like to try again? (Attempt ${newAttempts}/5)`,
            options: ["Yes", "No"],
            timestamp: Date.now(),
          })
          setShowOptions(true)
        }
      }, 800)
    } finally {
      setIsVerifyingContact(false)
    }
  }

  const handleOptionSelect = async (option) => {
    const lastBotMessage = messages[messages.length - 1]?.text || ""

    if (lastBotMessage.includes("Would you like to try again? (Attempt")) {
      if (option === "Yes") {
        setContact("")
        setShowContactInput(true)
        setShowOptions(false)
        safeSetMessage({
          type: "bot",
          text: `Please enter your registered contact number with ${clientName || "us"}:`,
        })
      } else if (option === "No") {
        safeSetMessage({ type: "user", text: "No" })
        safeSetMessage({
          type: "bot",
          text: "Thank you for using our services. Have a great day!",
        })
        setTimeout(() => {
          setShowRestartPrompt(true)
        }, 1000)
      }
      return
    }

    if (lastBotMessage.includes("What would you like to do next?")) {
      if (option === "Restart Conversation") {
        handleRestartConversation()
      } else if (option === "End Session") {
        safeSetMessage({ type: "user", text: "End Session" })
        safeSetMessage({ type: "bot", text: "Thank you for using our services. Have a great day!" })
        setTimeout(() => {
          setShowEndMenuOptions(true)
          setShowOptions(false)
        }, 1000)
      }
      return
    }

    // Initial flow selection
    if (
      lastBotMessage.includes("Please choose how we can assist you:") &&
      (option === "I am an existing AMC / warranty customer" || option === "I am looking for a new Sales or Service")
    ) {
      setIsExistingClient(option === "I am an existing AMC / warranty customer")

      if (option === "I am an existing AMC / warranty customer") {
        safeSetMessage({
          type: "bot",
          text: `Please enter your registered contact number with ${clientName || "us"}:`,
        })
        setShowContactInput(true)
        setShowOptions(false)
        setContactAttempts(0)
      } else if (option === "I am looking for a new Sales or Service") {
        setShowOptions(false)
        setIsExistingClient(false)
        await initializeSalesFlow()
      }
      return
    }

    // Service flow: after contact verification
    if (lastBotMessage === "What service are you looking for?") {
      setFlowSelection(option === "Break Down" ? "breakdown" : "rms")

      if (option === "Break Down") {
        try {
          const deviceRes = await getUserDevices(userId)
          const devices =
            deviceRes?.data?.map((item) => ({
              deviceID: item.deviceID,
              productId: item.productId,
              productName: item.ProductName,
            })) || []

          if (devices.length === 1) {
            await handleDeviceSelect(devices[0].deviceID, devices[0].productId)
          } else if (devices.length > 1) {
            safeSetMessage({ type: "bot", text: "Please select a device:" })
            setMessages((prev) => [...prev, { type: "device-options", devices, timestamp: Date.now() }])
          } else {
            safeSetMessage({ type: "bot", text: `No devices found for your account with ${clientName || "us"}.` })
            setTimeout(() => showEndMenu(), 1000)
          }
        } catch (err) {
          console.error("Error fetching devices:", err)
          safeSetMessage({ type: "bot", text: "Failed to fetch devices. Please try again." })
        }
      } else if (option === "RMS (Routine Maintenance Service)") {
        safeSetMessage({ type: "bot", text: "Fetching RMS details..." })
        try {
          const rmsRes = await getRMSAreasAndDevices(verifiedContact, clientId)

          if (rmsRes.status && rmsRes.data && rmsRes.data.length > 0) {
            const uniqueAreas = []
            const seenAreaCodes = new Set()

            rmsRes.data.forEach((item) => {
              if (!seenAreaCodes.has(item.area_code)) {
                uniqueAreas.push(item)
                seenAreaCodes.add(item.area_code)
              }
            })

            setRmsAreas(uniqueAreas)
            safeSetMessage({ type: "bot", text: "Please select an area for RMS:" })
            setMessages((prev) => [...prev, { type: "rms-area-options", areas: uniqueAreas, timestamp: Date.now() }])
          } else {
            safeSetMessage({
              type: "bot",
              text: `No active RMS plans found for your account with ${clientName || "us"}.`,
            })
            setTimeout(() => showEndMenu(), 1000)
          }
        } catch (err) {
          console.error("Error fetching RMS:", err)
          safeSetMessage({ type: "bot", text: "Failed to fetch RMS details. Please try again." })
          setTimeout(() => showEndMenu(), 1000)
        }
        setShowOptions(false)
        return
      }
    }

    // Handle Schedule RMS
    if (option === "Schedule RMS") {
      await handleScheduleRMS()
      return
    }

    // AMC Expired flow
    if (lastBotMessage.includes("Your AMC for this device has expired")) {
      if (option === "Renew AMC") {
        safeSetMessage({
          type: "bot",
          text: `Thank you for choosing AMC renewal with ${clientName || "us"}. Our team will reach out to you shortly.`,
        })
        try {
          await createAmcExpiredTicket({
            deviceID: selectedDevice?.deviceID,
            complaintIDs: storedComplaintIDs,
            createdBy: userId || 0,
          })
        } catch (err) {
          console.error("Create AMC Expired Ticket error:", err)
        }
        setTimeout(() => showEndMenu(), 1500)
      } else if (option === "Go with Call Basis Services") {
        safeSetMessage({
          type: "bot",
          text: `A service call has been logged on call basis with ${clientName || "us"}. Our team will contact you shortly.`,
        })
        try {
          await createAmcCallServiceTicket({
            deviceID: selectedDevice?.deviceID,
            complaintIDs: storedComplaintIDs,
            createdBy: userId || 0,
          })
        } catch (err) {
          console.error("Create AMC Call Service Ticket error:", err)
        }
        setTimeout(() => showEndMenu(), 1500)
      }
      setAmcStep("")
      setShowOptions(false)
      return
    }

    // Same/Other device after ticket
    if (lastBotMessage.includes("Do you have any issue regarding same device?")) {
      if (option === "Re-initiate Conversation") {
        handleRestartConversation()
      } else if (option === "End Session") {
        safeSetMessage({ type: "user", text: "End Session" })
        safeSetMessage({ type: "bot", text: "Thank you for using our services. Have a great day!" })
        setTimeout(() => {
          setShowEndMenuOptions(true)
          setShowOptions(false)
        }, 1000)
      }
      return
    }

    // Restart option
    if (option === "Restart Conversation") {
      handleRestartConversation()
    }
  }

  // ============================================
  // RESTART & RESET
  // ============================================
  const handleRestartConversation = () => {
    const welcomeMessage = clientName
      ? `Welcome to our ${clientName} Bot`
      : "Welcome to our Support System"

    setMessages([{ type: "bot", text: welcomeMessage, timestamp: Date.now() }])
    setShowOptions(false)
    setShowContactInput(false)
    setSelectedDevice(null)
    setIsExistingClient(null)
    setFlowSelection(null)
    setRmsAreas([])
    setSelectedArea(null)
    setCurrentQuestion(null)
    setQuestionHistory([])
    setIsDetailsFormVisible(false)
    setIsDetailsSubmitted(false)
    setIsOtherSelected(false)
    setShowCallbackMessage(false)
    setCallbackPreference(null)
    setCallbackRequested(false)
    setSalesFlowActive(false)
    setUserInfo({ name: "", mobile: "", email: "" })
    setValidationErrors({})
    setShowEndMenuOptions(false)
    setContact("")
    setContactAttempts(0)
    setInitialMessagesSet(false)
    setIsQuerySubmitted(false)
    setShowPhoneCallAfterNoCallback(false)
    setUserQuery("")
    setShowRestartPrompt(false)
    setVerifiedContact("")

    setTimeout(() => {
      setInitialMessagesSet(false)
      showInitialMenu()
    }, 500)
  }

  const handleRestartResponse = (response) => {
    playNotificationSound()
    if (response === "Yes") {
      handleRestartConversation()
    } else {
      safeSetMessage({ type: "bot", text: "Thank you for using our services. Have a great day!" })
      setTimeout(() => setShowEndMenuOptions(true), 1000)
    }
    setShowRestartPrompt(false)
  }

  // ============================================
  // RENDER
  // ============================================
  if (!isClientLoaded) return <div className="loading-container">Loading...</div>

  return (
    <div className="chat-container">
      <div className="chat-header">
        <strong>{clientName ? `${clientName} Bot` : "Support Bot"}</strong>
      </div>

      <div className="chat-body">
        {messages.map((msg, idx) => {
          if (msg.type === "device-options") {
            return (
              <div key={`${idx}-${msg.timestamp}`} className="device-options-container">
                {msg.devices.map((d, i) => (
                  <button key={i} className="device-button" onClick={() => handleDeviceSelect(d.deviceID, d.productId)}>
                    {d.deviceID}
                  </button>
                ))}
              </div>
            )
          } else if (msg.type === "complaint-options") {
            return (
              <div key={`${idx}-${msg.timestamp}`} className="complaint-options-container">
                <BotMessage text="Select your issue:" />
                <div className="complaint-list">
                  {msg.complaints.map((c) => (
                    <div key={c.id} className="complaint-item">
                      <input
                        type={isExistingClient ? "checkbox" : "radio"}
                        id={`complaint-${c.id}`}
                        value={c.ComplaintDetail}
                        checked={
                          isExistingClient
                            ? selectedComplaintDetails.includes(c.ComplaintDetail)
                            : selectedComplaintDetail === c.ComplaintDetail
                        }
                        onChange={(e) => {
                          if (isExistingClient) {
                            setSelectedComplaintDetails(
                              e.target.checked
                                ? [...selectedComplaintDetails, c.ComplaintDetail]
                                : selectedComplaintDetails.filter((item) => item !== c.ComplaintDetail),
                            )
                          } else {
                            setSelectedComplaintDetail(e.target.value)
                          }
                        }}
                        className="complaint-radio"
                      />
                      <label htmlFor={`complaint-${c.id}`} className="complaint-label">
                        {c.ComplaintDetail}
                      </label>
                    </div>
                  ))}
                  <button
                    className="submit-complaint-button"
                    onClick={() => {
                      if (isExistingClient) {
                        handleComplaintSelect(selectedComplaintDetails)
                      } else {
                        handleComplaintSelect([selectedComplaintDetail])
                      }
                    }}
                    disabled={isExistingClient ? selectedComplaintDetails.length === 0 : !selectedComplaintDetail}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )
          } else if (msg.type === "rms-area-options") {
            return (
              <div key={`${idx}-${msg.timestamp}`} className="rms-area-options-container">
                {rmsAreas.map((area, i) => (
                  <button
                    key={i}
                    className="btn btn-outline-primary"
                    onClick={() => handleRMSAreaSelect(area.area_code)}
                    style={{ width: "100%", marginBottom: i < rmsAreas.length - 1 ? "10px" : "0" }}
                  >
                    {area.area_code} ({area.devices?.length || 0} devices)
                  </button>
                ))}
              </div>
            )
          } else if (msg.type === "bot" || msg.type === "working-hours") {
            return (
              <div key={`${idx}-${msg.timestamp}`} className="message-group">
                <BotMessage text={msg.text} />
                {msg.options && msg.options.length > 0 && showOptions && (
                  <div className="options-container">
                    {msg.options.map((opt, i) => (
                      <button
                        key={i}
                        className="option-button"
                        onClick={() => {
                          if (salesFlowActive && msg.options[0]?.id) {
                            handleSalesOptionClick(opt.question_text || opt, idx)
                          } else {
                            handleOptionSelect(opt)
                          }
                        }}
                      >
                        {opt.question_text || opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          } else if (msg.type === "user") {
            return <UserMessage key={`${idx}-${msg.timestamp}`} text={msg.text} />
          }
          return null
        })}

        {showContactInput && (
          <div className="contact-input-container">
            <label style={{ fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "8px" }}>
              Please enter your registered contact number with {clientName || "us"}:
            </label>
            <input
              type="text"
              placeholder="Enter 10-digit mobile"
              value={contact}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "")
                if (val.length <= 10) setContact(val)
              }}
              maxLength="10"
              className="contact-input"
            />
            <button onClick={handleContactSubmit} disabled={isVerifyingContact || !contact.trim()}>
              {isVerifyingContact ? "Verifying..." : "Submit"}
            </button>
            {validationErrors.contact && <div className="error-message">{validationErrors.contact}</div>}
          </div>
        )}

        {showCallbackMessage && (
          <div className="callback-container">
            <p>Do you want to submit your request</p>
            <div className="d-flex">
              <button className="btn btn-primary" onClick={() => handleCallbackSelection("Yes")}>
                Yes
              </button>
              <button className="btn btn-outline-secondary" onClick={() => handleCallbackSelection("No")}>
                No
              </button>
            </div>
          </div>
        )}

        {isOtherSelected && !isDetailsFormVisible && !isDetailsSubmitted && (
          <OtherQueryInput
            handleQuerySubmit={handleUserQuerySubmit}
            isQueryInputDisabled={isQueryInputDisabled}
          // optional: you can show placeholder based on pendingQuery in OtherQueryInput if supported
          />
        )}

        {/* User Details Form (LeadBot Sales Flow) */}
        {isDetailsFormVisible && !isDetailsSubmitted && (
          <div className="user-form-container">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={userInfo.name}
                onChange={(e) => {
                  setUserInfo((prev) => ({ ...prev, name: e.target.value }))
                  if (validationErrors.name) setValidationErrors((prev) => ({ ...prev, name: "" }))
                }}
                style={{ textAlign: "left" }}
              />
              {validationErrors.name && <div className="error-message">{validationErrors.name}</div>}
            </div>
            <div className="form-group">
              <label>Mobile Number *</label>
              <input
                type="text"
                placeholder="10-digit mobile"
                value={userInfo.mobile}
                maxLength="10"
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "")
                  if (val.length <= 10) {
                    setUserInfo((prev) => ({ ...prev, mobile: val }))
                    if (validationErrors.mobile) setValidationErrors((prev) => ({ ...prev, mobile: "" }))
                  }
                }}
                style={{ textAlign: "left" }}
              />
              {validationErrors.mobile && <div className="error-message">{validationErrors.mobile}</div>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                placeholder="Enter your email"
                value={userInfo.email}
                onChange={(e) => {
                  setUserInfo((prev) => ({ ...prev, email: e.target.value }))
                  if (validationErrors.email) setValidationErrors((prev) => ({ ...prev, email: "" }))
                }}
                style={{ textAlign: "left" }}
              />
              {validationErrors.email && <div className="error-message">{validationErrors.email}</div>}
            </div>
            <button className="form-submit-button" onClick={() => handleUserDetailsSubmit(userInfo)}>
              Submit Details
            </button>
          </div>
        )}

        {showRestartPrompt && (
          <div>
            <div className="alert alert-info text-center p-2 mt-2">
              <p className="mb-2">Would you like to start again?</p>
              <div className="d-flex justify-content-center gap-2">
                <button className="btn btn-primary btn-sm" onClick={() => handleRestartResponse("Yes")}>
                  Yes
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => handleRestartResponse("No")}>
                  No
                </button>
              </div>
            </div>
            {showPhoneCallAfterNoCallback && supportPhoneNumber && (
              <div className="text-center my-3">
                <a href={`tel:${supportPhoneNumber}`} className="btn btn-success d-inline-flex align-items-center">
                  <i className="bi bi-telephone me-2"></i> For Quick Support Call to {supportPhoneNumber}
                </a>
              </div>
            )}
          </div>
        )}

        {/* End Menu */}
        {showEndMenuOptions && (
          <div className="end-menu-container">
            <button className="btn btn-restart" onClick={handleRestartConversation}>
              Restart Conversation
            </button>
            <button className="btn btn-exit" onClick={() => setMessages([])}>
              Exit
            </button>
          </div>
        )}

        {/* Typing Effect */}
        {showTypingEffect && (
          <div className="typing-effect">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="chat-footer">⚡ Powered by ATai Support</div>
    </div>
  )
}
export default ChatContainer

// This is related to Ticketbot